import { OrderLine } from './order-line';
import { OrderTypeEnum } from './order-type.enum';
import { OrderLineType } from './order-line-type';
import { environment } from '../../../../environments/environment';
import { GiftCard } from '../../giftcard/models/giftcard';
import { DiscountCoupon } from '../../payment/models/discount-coupon';
import { ProductPricelist } from '../../payment/models/product-pricelist';
import { FloatHelper } from '../../../helpers/float-helper';
import { ProductStockInformation } from './product-stock-information';

const defaultSortOrder = [
  OrderLineType.SingleProduct,
  OrderLineType.GiftCard,
  OrderLineType.AdditionalProduct,
  OrderLineType.Fee,
  OrderLineType.Product,
];

export class Order {
  id: number;
  name: string;
  state: string;
  bookingDate: Date;
  orderLines: OrderLine[] = [];
  partnerId: number;
  description: string;
  guestOfHonorId: number;
  redeemedGiftAmounts: GiftCard[];
  coupons: DiscountCoupon[];

  date: Date;
  timeSlotName: string;
  minutes: number;
  canChangeTimeSlot: boolean;

  isCacheLinesQtyCalculated = false;
  paidFromPOS = false;
  isMultiOrder = false;

  packId: number;

  guestOrderKey: string;
  giftcardUrl: string;
  invoiceUrl: string;
  ticketUrl: string;

  private _orderType: OrderTypeEnum;
  private _productPricelist: ProductPricelist;

  constructor(orderType: OrderTypeEnum, productPricelist: ProductPricelist) {
    this._orderType = orderType;
    this._productPricelist = productPricelist;
  }

  static createFromAny(data: any): Order {
    if (data === null || data === undefined) {
      return null;
    }
    const pricelist = data.productPricelist ? ProductPricelist.createFromAny(data.productPricelist) : null;
    const order = new Order(data.orderType, pricelist);
    order.id = data.id;
    order.name = data.name;
    order.state = data.state;
    if (data.date) {
      order.date = new Date(data.date);
    }
    order.minutes = data.minutes;
    order.canChangeTimeSlot = data.canChangeTimeSlot;
    order.timeSlotName = data.timeSlotName;
    if (data.bookingDate) {
      order.bookingDate = new Date(data.bookingDate);
    }
    order.partnerId = data.partnerId;
    order.description = data.description;
    order.guestOfHonorId = data.guestOfHonorId;
    if (data.redeemedGiftAmounts) {
      order.redeemedGiftAmounts = data.redeemedGiftAmounts.map(x => GiftCard.createFromAny(x));
    }
    if (data.orderLines) {
      order.orderLines = data.orderLines.filter(x => !x.coupon).map(x => OrderLine.createFromAny(x));
      order.coupons = data.orderLines.filter(x => x.coupon).map(x => DiscountCoupon.createFromOrderLine(x));
    } else {
      order.orderLines = new Array<OrderLine>();
      order.coupons = new Array<DiscountCoupon>();
    }
    order.paidFromPOS = data.paidFromPOS;
    order.isMultiOrder = data.isMultiOrder;
    order.packId = data.packId;
    order.guestOrderKey = data.guestOrderKey;
    order.giftcardUrl = data.giftcardUrl;
    order.invoiceUrl = data.invoiceUrl;
    order.ticketUrl = data.ticketUrl;
    return order;
  }

  get isPaid(): boolean {
    return (this.state && this.state !== 'cancel' && this.state !== 'draft') || this.paidFromPOS;
  }

  get isNew(): boolean {
    return !this.id;
  }

  get orderType(): OrderTypeEnum {
    return this._orderType;
  }

  get productPricelist(): ProductPricelist {
    return this._productPricelist;
  }

  deleteOrderLine(line: OrderLine): void {
    const index = this.orderLines.indexOf(line);
    if (index > -1) {
      this.orderLines.splice(index, 1);
    }
  }

  reduceQuantityOrderLine(line: OrderLine, reduceQty: number = 1): void {
    const index = this.orderLines.indexOf(line);
    if (index > -1) {
      this.orderLines[index].quantity = this.orderLines[index].quantity - reduceQty;
      if (this.orderLines[index].quantity < 1) {
        this.deleteOrderLine(line);
      }
    }
  }

  clearOrderLines(): void {
    this.orderLines = [];
  }

  addTypedOrderLine(orderLine: OrderLine, createCopy: boolean = true): void {
    this.insertTypedOrderLine(orderLine, this.orderLines.length, createCopy);
  }

  insertTypedOrderLine(orderLine: OrderLine, position: number, createCopy: boolean = true): OrderLine {
    const lines = this.orderLines;
    orderLine = createCopy ? orderLine.copy() : orderLine;
    if (position >= lines.length) {
      lines.push(orderLine);
    } else {
      lines.splice(position, 0, orderLine);
    }

    return orderLine;
  }

  addOrderLine(
    orderLineType: OrderLineType,
    name: string,
    productId: number,
    price: number,
    taxRate: number,
    quantity: number = 1,
    readonly: boolean = false,
    bookingSlotId: number = null,
    groupSize: number = 0,
    pickupPointName: string = null,
    pickupPointId: number = null,
    pickupPointGroupName: string = null,
    pickupPointGroupId: number = null,
    eventDateStart: Date = null,
    bookingDate: Date = null,
  ): OrderLine {
    return this.insertOrderLine(
      orderLineType,
      this.orderLines.length,
      name,
      productId,
      price,
      taxRate,
      quantity,
      readonly,
      bookingSlotId,
      groupSize,
      pickupPointName,
      pickupPointId,
      pickupPointGroupName,
      pickupPointGroupId,
      eventDateStart,
      bookingDate,
    );
  }

  insertOrderLine(
    orderLineType: OrderLineType,
    position: number,
    name: string = null,
    productId: number,
    price: number,
    taxRate: number,
    quantity: number = 1,
    readonly: boolean = true,
    bookingSlotId: number = null,
    groupSize: number = 0,
    pickupPointName: string = null,
    pickupPointId: number = null,
    pickupPointGroupName: string = null,
    pickupPointGroupId: number = null,
    eventDateStart: Date = null,
    bookingDate: Date = null,
  ): OrderLine {
    const lines = this.orderLines;
    const orderLines = lines.filter(x => (x.productId === productId && x.price === price && x.eventDateStart === eventDateStart));
    let orderLine = orderLines.length > 0 ? orderLines[0] : null;
    if (orderLine == null) {
      orderLine = new OrderLine(
        -1,
        productId,
        name,
        price,
        taxRate,
        quantity,
        readonly,
        orderLineType,
        bookingSlotId,
      );
      orderLine.groupSize = groupSize;
      if (eventDateStart) {
        orderLine.eventDateStart = eventDateStart;
      } else {
        orderLine.eventDateStart = null;
      }
      if (bookingDate) {
        orderLine.bookingDate = bookingDate;
      }
      orderLine.pickupPointName = pickupPointName;
      orderLine.pickupPointId = pickupPointId;
      orderLine.pickupPointGroupName = pickupPointGroupName;
      orderLine.pickupPointGroupId = pickupPointGroupId;
      this.insertTypedOrderLine(orderLine, position, false);
    } else {
      orderLine.quantity += quantity;
    }

    return orderLine;
  }

  updatePartnersOrderLine(oldOrderLines: OrderLine[]): void {
    if (!oldOrderLines || oldOrderLines.length === 0 || this.orderLines == null || this.orderLines.length === 0) {
      return;
    }
    oldOrderLines.forEach(oldOrderLine => {
      if (oldOrderLine.partners) {
        const line = this.orderLines.find(value => value.productId === oldOrderLine.productId);
        if (line) {
          line.partners = oldOrderLine.partners;
        }
      }
    });
  }

  get totalAmount(): number {
    return this.getTotal((x: OrderLine) => x.amount);
  }

  get totalTax(): number {
    return this.getTotal((x: OrderLine) => x.tax);
  }

  get totalQuantity(): number {
    return this.getTotal((x: OrderLine) => x.quantity);
  }

  getOrderLineByProductId(productId: number): OrderLine {
    const result = this.orderLines.filter(x => x.productId === productId);
    return result.length > 0 ? result[0] : null;
  }

  sortOrderLines(...params: OrderLineType[]): void {
    if (this.orderLines == null || !this.orderLines.length) {
      return;
    }
    params = params.length ? params : defaultSortOrder;
    this.orderLines = this.orderLines.sort((a, b) => params.indexOf(a.orderLineType) - params.indexOf(b.orderLineType));
  }

  getAmount(...params: OrderLineType[]): number {
    return this.getTotal((x: OrderLine) => x.amount, ...params);
  }

  getQuantity(...params: OrderLineType[]): number {
    return this.getTotal((x: OrderLine) => x.quantity, ...params);
  }

  private getTotal(f: (OrderLine) => number, ...params: OrderLineType[]): number {
    const allLines = params.length === 0;
    const total = this.orderLines.reduce((pv, cv) => pv + (allLines || params.indexOf(cv.orderLineType) >= 0 ? f(cv) : 0), 0);
    return FloatHelper.fixFloatPoint(total);
  }

  get giftCardsQty(): number {
    let giftCardsQty = 0;
    if (this.orderLines.length > 0) {
      this.orderLines.forEach(orderLine => {
        if
          (orderLine.orderLineType === OrderLineType.GiftCard) { giftCardsQty = giftCardsQty + orderLine.quantity; }
      });
    }
    return giftCardsQty;
  }

  get isGiftCardType(): boolean {
    return this.orderType === OrderTypeEnum.Giftcard;
  }

  updateMultiOrderState(): void {
    if (!this.orderLines || this.orderLines.length === 0) {
      this.isMultiOrder = false;
      return;
    }

    let firstCompareDate = null;
    let firstCompareBookingSlotId = null;
    for (const orderLine of this.orderLines) {
      if (!firstCompareDate && orderLine.bookingDate) {
        firstCompareDate = orderLine.bookingDate;
      }
      if (!firstCompareBookingSlotId && orderLine.bookingSlotId) {
        firstCompareBookingSlotId = orderLine.bookingSlotId;
      }

      if ((firstCompareDate
        && orderLine.bookingDate
        && firstCompareDate.getTime() !== orderLine.bookingDate.getTime())
        || (firstCompareBookingSlotId
          && orderLine.bookingSlotId
          && firstCompareBookingSlotId !== orderLine.bookingSlotId)) {
        this.isMultiOrder = true;
        return;
      }
    }

    this.isMultiOrder = false;
  }

  toJSON(): any {
    let bookingDate = this.bookingDate;
    if (bookingDate) {
      bookingDate = environment.globalSettings.getIsoDateStr(
        this.bookingDate.getFullYear(),
        this.bookingDate.getMonth() + 1,
        this.bookingDate.getDate());
    }

    return {
      id: this.id,
      name: this.name,
      state: this.state,
      bookingDate,
      orderLines: this.orderLines,
      orderType: this._orderType,
      partnerId: this.partnerId,
      description: this.description,
      guestOfHonorId: this.guestOfHonorId,
      redeemedGiftAmounts: this.redeemedGiftAmounts,
      isMultiOrder: this.isMultiOrder,
      packId: this.packId,
    };
  }

  getStockProductRequestQuantity(): ProductStockInformation[] {
    const requestProductsInfos = {};
    for (const line of this.orderLines) {
      if (!line.isStockProduct) {
        continue;
      }

      const requestProductsInfosByGroup = requestProductsInfos[line.pickupPointGroupId] || {};
      let quantity = requestProductsInfosByGroup[line.productId] || 0;
      quantity += line.quantity;
      requestProductsInfosByGroup[line.productId] = quantity;

      requestProductsInfos[line.pickupPointGroupId] = requestProductsInfosByGroup;
    }

    const result = [];
    for (const locationId in requestProductsInfos) {
      if (requestProductsInfos.hasOwnProperty(locationId)) {
        const requestProductsInfosByGroup = requestProductsInfos[locationId];
        if (!requestProductsInfosByGroup) {
          continue;
        }

        for (const productId in requestProductsInfosByGroup) {
          if (requestProductsInfosByGroup.hasOwnProperty(productId)) {
            result.push(new ProductStockInformation(parseInt(productId), requestProductsInfosByGroup[productId], parseInt(locationId)));
          }
        }
      }
    }

    return result;
  }

  getReservedProductCount(productId: number, ignoreDate: Date, locationId: number): number {
    let quantity = 0;
    for (const line of this.orderLines) {
      if (!line || line.productId !== productId || line.bookingDate === ignoreDate || (locationId && line.pickupPointGroupId !== locationId)) {
        continue;
      }

      quantity += line.quantity;
    }

    return quantity;
  }

  setAvailableProductQuantity(productId: number, quantity: number, locationId: number): string {
    let productName = '';
    for (const line of this.orderLines) {
      if (line.productId !== productId || line.pickupPointGroupId !== locationId) {
        continue;
      }

      productName = line.name;

      if (line.quantity <= quantity) {
        quantity -= line.quantity;
      } else {
        line.quantity = quantity;
        quantity = 0;
      }
    }

    return productName;
  }

  private _isChanged: boolean;

  get isChanged(): boolean {
    return this._isChanged || this.orderLines?.length !== 0;
  }

  set isChanged(value: boolean) {
    this._isChanged = value;
  }
}
